import React, { useState, useEffect, useCallback } from 'react';
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  FileText,
  Video,
  Link as LinkIcon,
  Presentation,
  File,
  Download,
  Loader2,
  ExternalLink,
  FolderOpen,
  Eye,
  X,
  Image,
  FileSpreadsheet,
  Archive,
  AlertCircle,
} from 'lucide-react';
import { useCourseContent } from '../../hooks/useStudent';
import type { CourseMaterialResponse } from '@/types/student.types';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import { createSafeHtml } from '@/utils/sanitize';
import { getFullFileUrl } from '@/utils/fileUrl';

interface ContentTabProps {
  moduleElementId: string;
  // Past year = read-only consultation; absent = current year
  academicYearId?: string;
  // Course instance (the student's group, or a former one)
  groupeId?: string;
}

// Office document preview state
interface OfficePreviewState {
  loading: boolean;
  error: string | null;
  content: string | null; // HTML content for Word
  excelData: { sheets: { name: string; data: string[][] }[]; activeSheet: number } | null;
}

const ContentTab: React.FC<ContentTabProps> = ({ moduleElementId, academicYearId, groupeId }) => {
  const { data: content, isLoading, error } = useCourseContent(moduleElementId, academicYearId, groupeId);
  const [expandedChapters, setExpandedChapters] = useState<Set<string>>(new Set());
  const [previewModal, setPreviewModal] = useState<{ open: boolean; material: CourseMaterialResponse | null }>({
    open: false,
    material: null,
  });
  const [officePreview, setOfficePreview] = useState<OfficePreviewState>({
    loading: false,
    error: null,
    content: null,
    excelData: null,
  });

  const toggleChapter = (chapterId: string) => {
    setExpandedChapters((prev) => {
      const next = new Set(prev);
      if (next.has(chapterId)) {
        next.delete(chapterId);
      } else {
        next.add(chapterId);
      }
      return next;
    });
  };

  // Get file extension
  const getFileExtension = (fileName: string | null | undefined): string => {
    if (!fileName) return '';
    return fileName.split('.').pop()?.toLowerCase() || '';
  };

  // Check if it's a Word document
  const isWordDocument = (fileName: string | null | undefined): boolean => {
    const ext = getFileExtension(fileName);
    return ['doc', 'docx'].includes(ext);
  };

  // Check if it's an Excel document
  const isExcelDocument = (fileName: string | null | undefined): boolean => {
    const ext = getFileExtension(fileName);
    return ['xls', 'xlsx', 'csv'].includes(ext);
  };

  // Check if it's a PowerPoint document
  const isPowerPointDocument = (fileName: string | null | undefined): boolean => {
    const ext = getFileExtension(fileName);
    return ['ppt', 'pptx'].includes(ext);
  };

  // Convert base64 to ArrayBuffer
  const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
    // Remove data URL prefix if present
    const base64Data = base64.includes(',') ? base64.split(',')[1] : base64;
    const binaryString = window.atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  // Load Word document
  const loadWordDocument = useCallback(async (fileUrl: string) => {
    setOfficePreview({ loading: true, error: null, content: null, excelData: null });
    try {
      const arrayBuffer = base64ToArrayBuffer(fileUrl);
      const result = await mammoth.convertToHtml({ arrayBuffer });
      setOfficePreview({
        loading: false,
        error: null,
        content: result.value,
        excelData: null,
      });
    } catch (err) {
      console.error('Error loading Word document:', err);
      setOfficePreview({
        loading: false,
        error: 'Impossible de charger le document Word. Veuillez le télécharger pour le visualiser.',
        content: null,
        excelData: null,
      });
    }
  }, []);

  // Load Excel document
  const loadExcelDocument = useCallback(async (fileUrl: string) => {
    setOfficePreview({ loading: true, error: null, content: null, excelData: null });
    try {
      const arrayBuffer = base64ToArrayBuffer(fileUrl);
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });

      const sheets = workbook.SheetNames.map((name) => {
        const worksheet = workbook.Sheets[name];
        const jsonData = XLSX.utils.sheet_to_json<string[]>(worksheet, { header: 1 });
        return { name, data: jsonData as string[][] };
      });

      setOfficePreview({
        loading: false,
        error: null,
        content: null,
        excelData: { sheets, activeSheet: 0 },
      });
    } catch (err) {
      console.error('Error loading Excel document:', err);
      setOfficePreview({
        loading: false,
        error: 'Impossible de charger le fichier Excel. Veuillez le télécharger pour le visualiser.',
        content: null,
        excelData: null,
      });
    }
  }, []);

  // Load Office document when modal opens
  useEffect(() => {
    if (previewModal.open && previewModal.material?.fileUrl) {
      const material = previewModal.material;

      if (isWordDocument(material.fileName) && material.fileUrl) {
        loadWordDocument(getFullFileUrl(material.fileUrl));
      } else if (isExcelDocument(material.fileName) && material.fileUrl) {
        loadExcelDocument(getFullFileUrl(material.fileUrl));
      } else {
        // Reset office preview for non-office files
        setOfficePreview({ loading: false, error: null, content: null, excelData: null });
      }
    } else {
      // Reset when modal closes
      setOfficePreview({ loading: false, error: null, content: null, excelData: null });
    }
  }, [previewModal.open, previewModal.material, loadWordDocument, loadExcelDocument]);

  // Get icon for material type
  const getMaterialIcon = (type: string | undefined | null, fileName?: string | null) => {
    if (fileName) {
      const ext = getFileExtension(fileName);
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return Image;
      if (['xls', 'xlsx', 'csv'].includes(ext)) return FileSpreadsheet;
      if (['zip', 'rar', '7z', 'tar'].includes(ext)) return Archive;
      if (['ppt', 'pptx', 'key', 'odp'].includes(ext)) return Presentation;
    }

    switch ((type || '').toUpperCase()) {
      case 'PDF':
      case 'DOCUMENT':
        return FileText;
      case 'VIDEO':
        return Video;
      case 'LINK':
        return LinkIcon;
      case 'PRESENTATION':
        return Presentation;
      default:
        return File;
    }
  };

  // Get color for material type
  const getMaterialColor = (type: string | undefined | null, fileName?: string | null) => {
    if (fileName) {
      const ext = getFileExtension(fileName);
      if (['pdf'].includes(ext)) return 'text-red-600 bg-red-100';
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'text-pink-600 bg-pink-100';
      if (['xls', 'xlsx', 'csv'].includes(ext)) return 'text-green-600 bg-green-100';
      if (['ppt', 'pptx', 'key', 'odp'].includes(ext)) return 'text-orange-600 bg-orange-100';
      if (['doc', 'docx', 'txt', 'rtf'].includes(ext)) return 'text-blue-600 bg-blue-100';
      if (['mp4', 'avi', 'mov', 'mkv', 'webm'].includes(ext)) return 'text-purple-600 bg-purple-100';
      if (['zip', 'rar', '7z', 'tar'].includes(ext)) return 'text-amber-600 bg-amber-100';
    }

    switch ((type || '').toUpperCase()) {
      case 'PDF':
        return 'text-red-600 bg-red-100';
      case 'VIDEO':
        return 'text-purple-600 bg-purple-100';
      case 'LINK':
        return 'text-blue-600 bg-blue-100';
      case 'PRESENTATION':
        return 'text-orange-600 bg-orange-100';
      case 'DOCUMENT':
        return 'text-blue-600 bg-blue-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  // Format file size
  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Check if file can be previewed in browser
  const canPreview = (fileName: string | null | undefined, fileUrl: string | null | undefined): boolean => {
    if (!fileName || !fileUrl) return false;
    const ext = getFileExtension(fileName);
    // PDF, images, videos, and Office docs can be previewed
    return ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'mp4', 'webm', 'mov', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'ppt', 'pptx'].includes(ext);
  };

  // Get file type label
  const getFileTypeLabel = (fileName: string | null | undefined): string => {
    if (!fileName) return 'Fichier';
    const ext = getFileExtension(fileName);
    const labels: Record<string, string> = {
      pdf: 'PDF',
      doc: 'Word',
      docx: 'Word',
      ppt: 'PowerPoint',
      pptx: 'PowerPoint',
      xls: 'Excel',
      xlsx: 'Excel',
      csv: 'CSV',
      jpg: 'Image',
      jpeg: 'Image',
      png: 'Image',
      gif: 'Image',
      mp4: 'Vidéo',
      webm: 'Vidéo',
      mov: 'Vidéo',
      zip: 'Archive',
      rar: 'Archive',
    };
    return labels[ext] || ext.toUpperCase() || 'Fichier';
  };

  // Handle download
  const handleDownload = async (material: CourseMaterialResponse) => {
    if (!material.fileUrl) return;

    try {
      if (material.fileUrl.startsWith('data:')) {
        const response = await fetch(getFullFileUrl(material.fileUrl));
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = material.fileName || material.title || 'document';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        const link = document.createElement('a');
        link.href = getFullFileUrl(material.fileUrl);
        link.download = material.fileName || material.title || 'document';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Download failed:', error);
      window.open(getFullFileUrl(material.fileUrl), '_blank');
    }
  };

  // Render Excel table
  const renderExcelPreview = () => {
    if (!officePreview.excelData) return null;

    const { sheets, activeSheet } = officePreview.excelData;
    const currentSheet = sheets[activeSheet];

    return (
      <div className="h-full flex flex-col">
        {/* Sheet tabs */}
        {sheets.length > 1 && (
          <div className="flex gap-1 p-2 bg-gray-200 border-b overflow-x-auto">
            {sheets.map((sheet, index) => (
              <button
                key={sheet.name}
                onClick={() => setOfficePreview(prev => ({
                  ...prev,
                  excelData: prev.excelData ? { ...prev.excelData, activeSheet: index } : null
                }))}
                className={`px-3 py-1.5 text-sm rounded-t-lg whitespace-nowrap transition-colors ${
                  index === activeSheet
                    ? 'bg-white text-green-700 font-medium'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {sheet.name}
              </button>
            ))}
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-auto bg-white">
          <table className="border-collapse min-w-full">
            <tbody>
              {currentSheet.data.map((row, rowIndex) => (
                <tr key={rowIndex} className={rowIndex === 0 ? 'bg-green-50 font-medium' : 'hover:bg-gray-50'}>
                  {/* Row number */}
                  <td className="px-2 py-1 text-xs text-gray-400 bg-gray-100 border border-gray-200 text-center min-w-[40px]">
                    {rowIndex + 1}
                  </td>
                  {row.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      className={`px-3 py-2 border border-gray-200 text-sm ${
                        rowIndex === 0 ? 'bg-green-50 font-medium text-green-900' : 'text-gray-700'
                      }`}
                    >
                      {cell !== undefined && cell !== null ? String(cell) : ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Render Word document
  const renderWordPreview = () => {
    if (!officePreview.content) return null;

    return (
      <div className="h-full overflow-auto bg-white p-8">
        <div
          className="prose prose-sm max-w-none"
          dangerouslySetInnerHTML={createSafeHtml(officePreview.content)}
          style={{
            fontFamily: 'Calibri, Arial, sans-serif',
            lineHeight: '1.6',
          }}
        />
      </div>
    );
  };

  // Render preview modal
  const renderPreviewModal = () => {
    if (!previewModal.open || !previewModal.material) return null;

    const material = previewModal.material;
    const ext = getFileExtension(material.fileName);
    const isPdf = ext === 'pdf';
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
    const isVideo = ['mp4', 'webm', 'mov'].includes(ext);
    const isWord = isWordDocument(material.fileName);
    const isExcel = isExcelDocument(material.fileName);
    const isPpt = isPowerPointDocument(material.fileName);

    const Icon = getMaterialIcon(material.type, material.fileName);
    const colorClass = getMaterialColor(material.type, material.fileName);

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden">
          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${colorClass}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{material.title}</h3>
                <p className="text-xs text-gray-500">
                  {material.fileName && `${material.fileName} • `}
                  {getFileTypeLabel(material.fileName)}
                  {material.fileSize && ` • ${formatFileSize(material.fileSize)}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {material.fileUrl && (
                <button
                  onClick={() => handleDownload(material)}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Télécharger
                </button>
              )}
              <button
                onClick={() => setPreviewModal({ open: false, material: null })}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Preview Content */}
          <div className="flex-1 overflow-hidden bg-gray-100">
            {/* Loading state for Office documents */}
            {officePreview.loading && (
              <div className="flex flex-col items-center justify-center h-full">
                <Loader2 className="h-10 w-10 animate-spin text-blue-600 mb-4" />
                <p className="text-gray-600">Chargement du document...</p>
              </div>
            )}

            {/* Error state */}
            {officePreview.error && (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <AlertCircle className="h-16 w-16 mb-4 text-amber-500" />
                <p className="text-lg font-medium">Erreur de chargement</p>
                <p className="text-sm mt-1 text-center max-w-md">{officePreview.error}</p>
                {material.fileUrl && (
                  <button
                    onClick={() => handleDownload(material)}
                    className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    Télécharger pour visualiser
                  </button>
                )}
              </div>
            )}

            {/* PDF Preview */}
            {isPdf && material.fileUrl && !officePreview.loading && (
              <iframe
                src={getFullFileUrl(material.fileUrl)}
                className="w-full h-full"
                title={material.title}
              />
            )}

            {/* Image Preview */}
            {isImage && material.fileUrl && (
              <div className="w-full h-full flex items-center justify-center p-4">
                <img
                  src={getFullFileUrl(material.fileUrl)}
                  alt={material.title}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                />
              </div>
            )}

            {/* Video Preview */}
            {isVideo && material.fileUrl && (
              <div className="w-full h-full flex items-center justify-center p-4">
                <video
                  src={getFullFileUrl(material.fileUrl)}
                  controls
                  className="max-w-full max-h-full rounded-lg shadow-lg"
                >
                  Votre navigateur ne supporte pas la lecture vidéo.
                </video>
              </div>
            )}

            {/* Word Document Preview */}
            {isWord && !officePreview.loading && !officePreview.error && officePreview.content && (
              renderWordPreview()
            )}

            {/* Excel Document Preview */}
            {isExcel && !officePreview.loading && !officePreview.error && officePreview.excelData && (
              renderExcelPreview()
            )}

            {/* PowerPoint - Cannot preview, show download message */}
            {isPpt && !officePreview.loading && (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <Presentation className="h-16 w-16 mb-4 text-orange-500" />
                <p className="text-lg font-medium">Aperçu PowerPoint</p>
                <p className="text-sm mt-1 text-center max-w-md">
                  Les présentations PowerPoint ne peuvent pas être affichées dans le navigateur.
                  <br />
                  Veuillez télécharger le fichier pour le visualiser.
                </p>
                {material.fileUrl && (
                  <button
                    onClick={() => handleDownload(material)}
                    className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    Télécharger la présentation
                  </button>
                )}
              </div>
            )}

            {/* Fallback for unsupported files */}
            {!isPdf && !isImage && !isVideo && !isWord && !isExcel && !isPpt && !officePreview.loading && (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <File className="h-16 w-16 mb-4 text-gray-400" />
                <p className="text-lg font-medium">Aperçu non disponible</p>
                <p className="text-sm mt-1 text-center max-w-md">
                  {ext ? (
                    <>
                      Les fichiers <span className="font-medium">{ext.toUpperCase()}</span> ne peuvent pas être prévisualisés dans le navigateur.
                    </>
                  ) : (
                    'Ce type de fichier ne peut pas être prévisualisé.'
                  )}
                </p>
                {material.fileUrl && (
                  <button
                    onClick={() => handleDownload(material)}
                    className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    Télécharger pour visualiser
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render a material item
  const renderMaterial = (material: CourseMaterialResponse) => {
    const Icon = getMaterialIcon(material.type, material.fileName);
    const colorClass = getMaterialColor(material.type, material.fileName);
    // A link material: declared as such, or carrying only an external address
    const isLink = (material.type || '').toUpperCase() === 'LINK' || (!!material.externalUrl && !material.fileUrl);
    const hasFile = !!material.fileUrl;
    const canPreviewFile = canPreview(material.fileName, material.fileUrl);

    return (
      <div
        key={material.id}
        className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all"
      >
        <div className={`p-2 rounded-lg ${colorClass}`}>
          <Icon className="h-5 w-5" />
        </div>

        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-900 truncate">{material.title}</h4>
          {material.description && (
            <p className="text-sm text-gray-500 truncate">{material.description}</p>
          )}
          <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
            <span>{getFileTypeLabel(material.fileName)}</span>
            {material.fileSize && (
              <>
                <span>•</span>
                <span>{formatFileSize(material.fileSize)}</span>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1">
          {/* External link button */}
          {isLink && material.externalUrl && (
            <a
              href={material.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Ouvrir le lien"
            >
              <ExternalLink className="h-5 w-5" />
            </a>
          )}

          {/* Preview/Expand button for files */}
          {hasFile && (
            <button
              onClick={() => setPreviewModal({ open: true, material })}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title={canPreviewFile ? 'Voir le fichier' : 'Détails du fichier'}
            >
              <Eye className="h-5 w-5" />
            </button>
          )}

          {/* Download button for files */}
          {hasFile && (
            <button
              onClick={() => handleDownload(material)}
              className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title="Télécharger"
            >
              <Download className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        Erreur lors du chargement du contenu
      </div>
    );
  }

  if (!content || (content.chapters.length === 0 && content.generalMaterials.length === 0)) {
    return (
      <div className="text-center py-12">
        <FolderOpen className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">Aucun contenu disponible</h3>
        <p className="mt-2 text-sm text-gray-500">
          Le professeur n'a pas encore publié de contenu pour ce cours.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <BookOpen className="h-5 w-5 text-blue-600" />
        Contenu du cours
      </h2>

      {/* Chapters */}
      {content.chapters.map((chapter) => {
        const isExpanded = expandedChapters.has(chapter.id);

        return (
          <div key={chapter.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Chapter Header */}
            <button
              onClick={() => toggleChapter(chapter.id)}
              className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronRight className="h-5 w-5 text-gray-500" />
              )}
              <div className="flex-1 text-left">
                <h3 className="font-semibold text-gray-900">{chapter.title}</h3>
                {chapter.description && (
                  <p className="text-sm text-gray-500">{chapter.description}</p>
                )}
              </div>
              <span className="text-sm text-gray-400">
                {chapter.materials.length} ressource{chapter.materials.length !== 1 ? 's' : ''}
              </span>
            </button>

            {/* Chapter Materials */}
            {isExpanded && chapter.materials.length > 0 && (
              <div className="border-t border-gray-200 p-4 space-y-3 bg-gray-50">
                {chapter.materials.map(renderMaterial)}
              </div>
            )}
          </div>
        );
      })}

      {/* General Materials */}
      {content.generalMaterials.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-purple-50">
            <h3 className="font-semibold text-purple-900">{content.generalSectionTitle}</h3>
          </div>
          <div className="p-4 space-y-3">
            {content.generalMaterials.map(renderMaterial)}
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {renderPreviewModal()}
    </div>
  );
};

export default ContentTab;
